import { useState, useEffect, useCallback } from "react";
import Cookies from "js-cookie";
import { ACCESS_LEVELS, useUser } from "../../services/hooks/useUser";

/**
 * Custom hook for managing poll popup display logic
 *
 * Features:
 * - Manages popup visibility based on display count
 * - Tracks display count when popup is opened
 * - Resets counter when popup ID changes
 * - Stores metadata in cookies
 *
 * @param {Object} options
 * @param {Object} options.poll - Poll data object from API
 * @param {boolean} options.isLoading - Loading state from poll fetch
 * @param {boolean} options.isError - Error state from poll fetch
 * @param {number} options.delaySeconds - Delay in seconds before showing popup (default: 5)
 * @param {number} options.maxDisplayCount - Maximum times to show popup (default: 5)
 * @param {string} options.cookieName - Cookie name for storing metadata (default: "survey_popup_metadata")
 *
 * @returns {Object} Object containing:
 *   - isOpen: boolean - Whether popup is currently open
 *   - shouldShow: boolean - Whether popup should be shown
 *   - handleClose: function - Close popup handler
 *   - handleDismiss: function - Dismiss popup handler (closes the popup)
 *   - metadata: object - Current popup metadata
 */
export function usePollPopup({
  poll,
  isLoading,
  isError,
  delaySeconds = 5,
  maxDisplayCount = 5,
  cookieName = "survey_popup_metadata",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const { user, isLoggedIn, isBnppClient } = useUser();
  /**
   * Get popup metadata from cookie
   * @returns {Object} Metadata object with displayCount and current popup ID
   */
  const getMetadata = useCallback(() => {
    try {
      const metadata = Cookies.get(cookieName);
      if (metadata) {
        return JSON.parse(metadata);
      }
    } catch (error) {
      console.error("Error parsing survey popup metadata:", error);
    }
    return {
      displayCount: 0,
      currentPopupId: null,
    };
  }, [cookieName, user]);

  /**
   * Save popup metadata to cookie
   * @param {Object} metadata - Metadata to save
   */
  const saveMetadata = useCallback(
    metadata => {
      try {
        Cookies.set(cookieName, JSON.stringify(metadata), { expires: 1 / 24 });
      } catch (error) {
        console.error("Error saving survey popup metadata:", error);
      }
    },
    [cookieName, user]
  );

  /**
   * Check if popup should be displayed based on display count
   * Resets counter if popup ID has changed
   * @returns {boolean}
   */
  const shouldDisplayPopup = useCallback(() => {
    if (!poll) return false;

    if (poll.publish_status !== 3) {
      return false;
    }

    if (poll.access === ACCESS_LEVELS.BNPP_CLIENT && !isBnppClient) {
      return false;
    }

    if (poll.access === ACCESS_LEVELS.LOGGED && !isLoggedIn) {
      return false;
    }

    // Don't show popup on survey/poll pages
    const currentPath = window.location.pathname;
    if (
      currentPath.includes("/ankiety/") ||
      currentPath.includes("/surveys/")
    ) {
      return false;
    }

    const metadata = getMetadata();

    // If popup ID is different from stored ID, reset counter
    if (metadata.currentPopupId !== poll.id) {
      return true;
    }

    // Check if max display count reached for current popup
    if (metadata.displayCount >= maxDisplayCount) {
      return false;
    }

    return true;
  }, [poll, getMetadata, maxDisplayCount]);

  /**
   * Handle popup display and increment counter
   * Resets counter if popup ID has changed
   */
  const displayPopup = useCallback(() => {
    if (!poll) return;

    if (poll.publish_status !== 3) {
      return;
    }

    if (poll.access === ACCESS_LEVELS.BNPP_CLIENT && !isBnppClient) {
      return;
    }

    if (poll.access === ACCESS_LEVELS.LOGGED && !isLoggedIn) {
      return;
    }

    const metadata = getMetadata();

    // If popup ID is different, reset counter and set new popup ID
    if (metadata.currentPopupId !== poll.id) {
      metadata.displayCount = 1;
      metadata.currentPopupId = poll.id;
    } else {
      // Increment counter for the same popup
      metadata.displayCount = (metadata.displayCount || 0) + 1;
    }

    saveMetadata(metadata);
    setIsOpen(true);
  }, [poll, getMetadata, saveMetadata, isBnppClient, isLoggedIn]);

  /**
   * Handle popup close
   */
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Handle popup dismiss (close the popup)
   */
  const handleDismiss = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Check if popup should be shown when poll data is loaded
  useEffect(() => {
    if (poll && !isLoading && !isError) {
      setShouldShow(shouldDisplayPopup());
    }
  }, [poll, isLoading, isError, shouldDisplayPopup, isLoggedIn, isBnppClient]);

  // Show popup after delay if conditions are met
  useEffect(() => {
    if (!shouldShow) return undefined;

    const timer = setTimeout(() => {
      displayPopup();
    }, delaySeconds * 1000);

    return () => clearTimeout(timer);
  }, [shouldShow, delaySeconds, displayPopup]);

  return {
    isOpen,
    shouldShow,
    handleClose,
    handleDismiss,
    metadata: getMetadata(),
  };
}
